import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Text, Flex, Button, Skeleton, SimpleGrid, useMediaQuery } from '@chakra-ui/react';
import { Search, UserCheck, UserX, Eye, X } from 'lucide-react';
import type { Applicant } from './useVolunteersPage';

interface WaitingListProps {
  applicants: Applicant[];
  loading: boolean;
  search: string;
  onSearchChange: (val: string) => void;
  onAccept: (applicant: Applicant) => void;
  onDeny: (applicant: Applicant) => void;
  onAcceptAll: () => void;
}

export function VolunteersWaitingList({
  applicants,
  loading,
  search,
  onSearchChange,
  onAccept,
  onDeny,
  onAcceptAll,
}: WaitingListProps) {
  const navigate = useNavigate();
  const [viewingAnswers, setViewingAnswers] = useState<Applicant | null>(null);
  const [isAtLeast600] = useMediaQuery(['(min-width: 600px)']);
  const [isAtLeast1024] = useMediaQuery(['(min-width: 1024px)']);
  const columns = isAtLeast1024 ? 3 : isAtLeast600 ? 2 : 1;

  const filtered = search.trim()
    ? applicants.filter((a) => a.name.toLowerCase().includes(search.trim().toLowerCase()))
    : applicants;

  if (loading) {
    return (
      <Box mt="2" display="flex" flexDirection="column" gap="3">
        {[0, 1, 2].map((k) => (
          <Box key={k} bg="volyx.bg" borderRadius="2xl" boxShadow="0 6px 18px rgba(0,0,0,0.12)" p="4">
            <Flex alignItems="center" gap="3" mb="3">
              <Skeleton borderRadius="full" w="44px" h="44px" flexShrink={0} />
              <Box flex="1">
                <Skeleton h="4" w="50%" mb="2" borderRadius="md" />
                <Skeleton h="3" w="30%" borderRadius="md" />
              </Box>
            </Flex>
            <Box
              display="grid"
              gridTemplateColumns="repeat(3, minmax(0, 1fr))"
              gap="2"
            >
              <Skeleton h="32px" borderRadius="lg" />
              <Skeleton h="32px" borderRadius="lg" />
              <Skeleton h="32px" borderRadius="lg" />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Search */}
      <Flex
        alignItems="center"
        bg="volyx.lightGray"
        borderRadius="xl"
        px="4"
        h="48px"
        mb="4"
        gap="3"
      >
        <Search size={18} style={{ color: 'var(--chakra-colors-volyx-textSecondary)', flexShrink: 0 }} />
        <input
          style={{
            flex: 1,
            border: 'none',
            background: 'transparent',
            outline: 'none',
            fontSize: '14px',
            color: 'var(--chakra-colors-volyx-primary)',
            fontFamily: 'inherit',
          }}
          placeholder="Search in waiting list by name..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </Flex>

      {/* Accept All */}
      {filtered.length > 0 && !search.trim() && (
        <Button
          w="full"
          bg="volyx.success"
          color="white"
          borderRadius="xl"
          fontWeight="semibold"
          mb="4"
          h="48px"
          border="none"
          cursor="pointer"
          fontSize="sm"
          boxShadow="0 4px 12px rgba(0,0,0,0.1)"
          _hover={{ opacity: 0.9, transform: 'translateY(-1px)', boxShadow: '0 8px 20px rgba(0,0,0,0.15)' }}
          transition="all 0.2s"
          onClick={onAcceptAll}
        >
          Accept All ({filtered.length})
        </Button>
      )}

      {/* Empty states */}
      {filtered.length === 0 && search.trim() && (
        <Box textAlign="center" py="10">
          <Text fontSize="sm" color="volyx.textSecondary">No applicants found</Text>
        </Box>
      )}
      {filtered.length === 0 && !search.trim() && (
        <Box
          textAlign="center"
          py="10"
          bg="volyx.bg"
          borderRadius="2xl"
          boxShadow="0 6px 18px rgba(0,0,0,0.08)"
          px="6"
        >
          <Text fontSize="sm" fontWeight="semibold" color="volyx.primary" mb="1">No applications yet</Text>
          <Text fontSize="xs" color="volyx.textSecondary">When volunteers apply to your event, they will appear here</Text>
        </Box>
      )}

      {/* Applicant cards */}
      <SimpleGrid columns={columns} gap={3}>
        {filtered.map((applicant) => (
          <Box
            key={applicant.id}
            bg="volyx.bg"
            borderRadius="2xl"
            boxShadow="0 6px 18px rgba(0,0,0,0.12)"
            p="4"
            transition="box-shadow 0.2s"
            _hover={{ boxShadow: '0 10px 28px rgba(0,0,0,0.14)' }}
          >
            <Flex alignItems="center" gap="3" mb="3">
              <Box
                as="button"
                w="44px"
                h="44px"
                borderRadius="full"
                overflow="hidden"
                bg="volyx.primaryLight"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                border="none"
                cursor="pointer"
                p="0"
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); navigate(`/profile?id=${applicant.volunteer_id}`); }}
              >
                {applicant.image ? (
                  <img src={applicant.image} alt={applicant.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Text fontSize="sm" fontWeight="bold" color="volyx.primary" m="0">
                    {applicant.name.split(' ').slice(0, 2).map((p) => p[0]?.toUpperCase()).join('')}
                  </Text>
                )}
              </Box>
              <Box flex="1" minW="0">
                <Box
                  as="button"
                  textAlign="left"
                  bg="transparent"
                  border="none"
                  cursor="pointer"
                  p="0"
                  w="fit-content"
                  maxW="100%"
                  onClick={(e: React.MouseEvent) => { e.stopPropagation(); navigate(`/profile?id=${applicant.volunteer_id}`); }}
                >
                  <Text fontSize="sm" fontWeight="semibold" color="volyx.primary" truncate m="0" _hover={{ textDecoration: 'underline' }}>{applicant.name}</Text>
                  <Text fontSize="xs" color="volyx.textSecondary" m="0" mt="0.5" display="block">{applicant.role}</Text>
                </Box>
              </Box>
            </Flex>

            <Box
              display="grid"
              gridTemplateColumns="repeat(3, minmax(0, 1fr))"
              gap="1.5"
            >
              <Button
                w="full"
                minW="0"
                h="34px"
                px="2"
                bg="volyx.lightGray"
                border="none"
                color="volyx.primary"
                borderRadius="lg"
                fontWeight="semibold"
                fontSize="11px"
                cursor="pointer"
                overflow="hidden"
                whiteSpace="nowrap"
                _hover={{ bg: 'volyx.mediumGray' }}
                onClick={() => setViewingAnswers(applicant)}
              >
                <Box as="span" display="inline-flex" alignItems="center" justifyContent="center" gap="1" minW="0" maxW="100%">
                  <Eye size={12} style={{ flexShrink: 0 }} />
                  <Text as="span" m="0" fontSize="inherit" fontWeight="inherit" lineHeight="1" truncate>
                    Answers
                  </Text>
                </Box>
              </Button>
              <Button
                w="full"
                minW="0"
                h="34px"
                px="2"
                bg="volyx.success"
                color="white"
                borderRadius="lg"
                fontWeight="semibold"
                fontSize="11px"
                border="none"
                cursor="pointer"
                overflow="hidden"
                whiteSpace="nowrap"
                _hover={{ opacity: 0.9 }}
                onClick={() => onAccept(applicant)}
              >
                <Box as="span" display="inline-flex" alignItems="center" justifyContent="center" gap="1" minW="0" maxW="100%">
                  <UserCheck size={12} style={{ flexShrink: 0 }} />
                  <Text as="span" m="0" fontSize="inherit" fontWeight="inherit" lineHeight="1" truncate>
                    Accept
                  </Text>
                </Box>
              </Button>
              <Button
                w="full"
                minW="0"
                h="34px"
                px="2"
                bg="transparent"
                border="1px solid"
                borderColor="volyx.error"
                color="volyx.error"
                borderRadius="lg"
                fontWeight="semibold"
                fontSize="11px"
                cursor="pointer"
                overflow="hidden"
                whiteSpace="nowrap"
                _hover={{ bg: '#FFF5F5' }}
                onClick={() => onDeny(applicant)}
              >
                <Box as="span" display="inline-flex" alignItems="center" justifyContent="center" gap="1" minW="0" maxW="100%">
                  <UserX size={12} style={{ flexShrink: 0 }} />
                  <Text as="span" m="0" fontSize="inherit" fontWeight="inherit" lineHeight="1" truncate>
                    Deny
                  </Text>
                </Box>
              </Button>
            </Box>
          </Box>
        ))}
      </SimpleGrid>

      {/* View Answers Modal */}
      {viewingAnswers && (
        <Box
          position="fixed"
          inset="0"
          bg="rgba(0,0,0,0.5)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={1000}
          onClick={() => setViewingAnswers(null)}
        >
          <Box
            bg="volyx.bg"
            borderRadius="2xl"
            maxW="440px"
            w="90%"
            maxH="80vh"
            display="flex"
            flexDirection="column"
            boxShadow="0 16px 56px rgba(0,0,0,0.18)"
            onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          >
            {/* Header */}
            <Flex
              px="5"
              py="4"
              justifyContent="space-between"
              alignItems="center"
              borderBottom="1px solid"
              borderColor="volyx.borderLight"
            >
              <Text fontSize="lg" fontWeight="bold" color="volyx.primary" m="0">Application Answers</Text>
              <Box
                as="button"
                bg="volyx.lightGray"
                border="none"
                cursor="pointer"
                w="32px"
                h="32px"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: 'volyx.mediumGray' }}
                onClick={() => setViewingAnswers(null)}
              >
                <X size={16} style={{ color: 'var(--chakra-colors-volyx-textSecondary)' }} />
              </Box>
            </Flex>

            {/* Body */}
            <Box flex="1" overflowY="auto" p="5">
              {/* Volunteer name */}
              <Flex
                as="button"
                alignItems="center"
                gap="3"
                mb="5"
                bg="transparent"
                border="none"
                cursor="pointer"
                p="0"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/profile?id=${viewingAnswers.volunteer_id}`);
                }}
              >
                <Box
                  w="40px"
                  h="40px"
                  borderRadius="full"
                  bg="volyx.primaryLight"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  overflow="hidden"
                >
                  {viewingAnswers.image ? (
                    <img src={viewingAnswers.image} alt={viewingAnswers.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <Text fontSize="sm" fontWeight="bold" color="volyx.primary" m="0">
                      {viewingAnswers.name.split(' ').slice(0, 2).map((p) => p[0]?.toUpperCase()).join('')}
                    </Text>
                  )}
                </Box>
                <Box textAlign="left">
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color="volyx.primary"
                    m="0"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    {viewingAnswers.name}
                  </Text>
                  <Text fontSize="xs" color="volyx.textSecondary" m="0">
                    {viewingAnswers.role}
                  </Text>
                </Box>
              </Flex>

              {/* Answers */}
              <Box display="flex" flexDirection="column" gap="4">
                {viewingAnswers.answers.map((ans, i) => (
                  <Box
                    key={i}
                    bg="volyx.lightGray"
                    borderRadius="xl"
                    px="4"
                    py="3"
                  >
                    <Text
                      fontSize="10px"
                      fontWeight="semibold"
                      color="volyx.textSecondary"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      m="0"
                      mb="1"
                    >
                      {ans.question}
                    </Text>
                    <Text fontSize="sm" fontWeight="medium" color="volyx.primary" m="0" whiteSpace="pre-line">
                      {ans.answer}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
